// FIX-05 — public contact page: form + email + mailing address + SLA + Discord.
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send — please email us directly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen px-6 py-10" style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-primary)" }}>
      <div className="max-w-2xl mx-auto" data-testid="contact-page">
        <Link to="/" className="text-[11px] font-bold tracking-wide opacity-70 hover:opacity-100 flex items-center gap-1.5"
          style={{ color: "var(--hoip-text-secondary)" }}>
          <Activity className="w-3.5 h-3.5 text-emerald-500" /> ← MarkovLab
        </Link>
        <h1 className="mt-4 text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Contact us</h1>
        <p className="text-xs mt-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="contact-sla">
          We respond to support enquiries within 2 business days.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <Mail className="w-3.5 h-3.5 mb-1.5 text-emerald-500" />
            <div className="font-bold mb-0.5">Email</div>
            <a href="mailto:info@markovlab.app" className="underline" data-testid="contact-email">info@markovlab.app</a>
          </div>
          <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <MessageCircle className="w-3.5 h-3.5 mb-1.5 text-emerald-500" />
            <div className="font-bold mb-0.5">Community</div>
            <a href="https://discord.gg/5mZwd9XUf" target="_blank" rel="noreferrer" className="underline" data-testid="contact-discord">Join our Discord</a>
          </div>
          <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <MapPin className="w-3.5 h-3.5 mb-1.5 text-emerald-500" />
            <div className="font-bold mb-0.5">Mail</div>
            <span style={{ color: "var(--hoip-text-secondary)" }} data-testid="contact-address">
              Concrete Control LLC (dba MarkovLab), 1209 Mountain Road Pl NE, Ste N, Albuquerque, NM 87110, USA
            </span>
          </div>
        </div>

        <div className="mt-6 border rounded-sm p-5" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          {sent ? (
            <div className="text-sm py-6 text-center" data-testid="contact-success">
              <Clock className="w-5 h-5 mx-auto mb-2 text-emerald-500" />
              Thanks — your message has been received. We'll reply within 2 business days.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              {error && <div className="text-xs p-2 border" style={{ background: "#1C1008", borderColor: "#78350F", color: "#FDE68A" }} data-testid="contact-error">{String(error)}</div>}
              <div>
                <Label className="text-xs mb-1 block">Name</Label>
                <Input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5] rounded-sm h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Email</Label>
                <Input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5] rounded-sm h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Message</Label>
                <textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} required minLength={5}
                  rows={5} className="w-full text-sm p-2 rounded-sm bg-[#0A0A0A] border border-[#27272A] text-[#F4F4F5]" />
              </div>
              <Button type="submit" disabled={busy} data-testid="contact-submit-btn"
                className="bg-white text-black hover:bg-gray-200 rounded-sm h-9 text-sm font-medium">
                {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </div>

        <div className="mt-8 flex items-center gap-4 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          <Link to="/legal/terms" className="underline">Terms</Link>
          <Link to="/legal/privacy" className="underline">Privacy</Link>
          <Link to="/legal/risk" className="underline">Risk Disclosure</Link>
          <Link to="/legal/refund" className="underline">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
}
